import React, {useState, useEffect, useRef} from 'react';
import {classNames} from 'primereact/utils';
import {useSelector, useDispatch} from 'react-redux';
import Topbar from './global/Topbar';
import Menu from './global/Menu';
import PrimeReact from 'primereact/api';
import {Tooltip} from 'primereact/tooltip';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ConfirmDialog} from 'primereact/confirmdialog';
import {
    faMapLocation,
    faGlobeAmericas,
    faImages,
    faChartBar,
    faFolderOpen,
} from '@fortawesome/free-solid-svg-icons';
import {isTablet} from '../components/functions/Global';
import Chart from "./Chart";
import Boreholes3D from "./Boreholes3D";
import CoreViewer from "./CoreViewer";
import GeoMap from "./GeoMap";
import ProjectOverview from "./ProjectOverview";
import {RootState} from "../redux/store";
import {parseUrl, navigate, onUrlChange, UrlState} from "../functions/urlHash";
import {setSelectedProject} from "../redux/slices/ProjectSlice";
import {useGetProjectsQuery} from "../redux/rtk/drillholeApi";

const App = () => {
    const [rightMenuActive, setRightMenuActive] = useState(false);
    const [configActive, setConfigActive] = useState(false);
    const [menuMode, setMenuMode] = useState('sidebar');
    const [overlayMenuActive, setOverlayMenuActive] = useState(false);
    const [ripple, setRipple] = useState(true);
    const [sidebarStatic, setSidebarStatic] = useState(() => localStorage.getItem('SidebarStatic') === 'true');
    const [staticMenuDesktopInactive, setStaticMenuDesktopInactive] = useState(false);
    const [staticMenuMobileActive, setStaticMenuMobileActive] = useState(false);
    const [menuActive, setMenuActive] = useState(false);
    const [searchActive, setSearchActive] = useState(false);
    const [topbarMenuActive, setTopbarMenuActive] = useState(false);
    const [sidebarActive, setSidebarActive] = useState(() => localStorage.getItem('SidebarStatic') === 'true');
    const [pinActive, setPinActive] = useState(false);
    const [activeInlineProfile, setActiveInlineProfile] = useState(false);
    const [resetActiveIndex, setResetActiveIndex] = useState<boolean>(false);
    const [customBreadcrumb, setCustomBreadcrumb] = useState<any>(null);
    const [urlState, setUrlState] = useState<UrlState>(parseUrl());
    const [boreholes3DMounted, setBoreholes3DMounted] = useState<boolean>(parseUrl().view === 'boreholes3d');
    const selectedProject = useSelector((state: RootState) => state.project.selectedProject);
    const hasProject = Boolean(selectedProject);
    const dispatch = useDispatch();
    const {data: projects} = useGetProjectsQuery();
    const copyTooltipRef = useRef<any>();

    PrimeReact.ripple = true;

    const theme = localStorage.getItem('Theme');
    const [colorScheme, setColorScheme] = useState(theme !== null && theme !== 'undefined' ? theme : 'dark');

    useEffect(() => {
        localStorage.setItem('SidebarStatic', String(sidebarStatic));
    }, [sidebarStatic]);

    useEffect(() => {
        // Default-Route: ohne View → projects (oder geomap, wenn Projekt im Pfad steht)
        if (!urlState.view) {
            navigate({view: urlState.projectId ? 'geomap' : 'projects'});
        }
    }, []);

    // Wenn kein Projekt im Pfad ist, andere Views verbergen
    useEffect(() => {
        if (!urlState.projectId && urlState.view && urlState.view !== 'projects') {
            navigate({view: 'projects', projectId: null});
        }
    }, [urlState.projectId, urlState.view]);

    // URL → Redux: Projekt-Auswahl aus dem Pfad lesen und Redux synchronisieren
    useEffect(() => {
        const projectIdInUrl = urlState.projectId;
        const currentId = selectedProject?.project_id ?? null;
        if (projectIdInUrl !== currentId) {
            if (projectIdInUrl) {
                dispatch(setSelectedProject({
                    project_id: projectIdInUrl,
                    name: selectedProject?.project_id === projectIdInUrl ? selectedProject?.name ?? projectIdInUrl : projectIdInUrl,
                }));
            } else {
                dispatch(setSelectedProject(null));
            }
        }
    }, [urlState.projectId, selectedProject?.project_id, selectedProject?.name, dispatch]);

    useEffect(() => {
        return onUrlChange(() => setUrlState(parseUrl()));
    }, []);

    // On reload the project is restored from the URL with only its id (the name
    // falls back to the id). Once the projects list is available, re-derive the
    // real name so the header always shows it. Only runs while the name is still
    // the id fallback, so a fresh rename is never overwritten by a stale cache.
    useEffect(() => {
        if (!selectedProject || !projects) return;
        if (selectedProject.name !== selectedProject.project_id) return;
        const match = projects.find(p => p.project_id === selectedProject.project_id);
        if (match && match.name && match.name !== selectedProject.name) {
            dispatch(setSelectedProject({project_id: match.project_id, name: match.name}));
        }
    }, [projects, selectedProject?.project_id, selectedProject?.name, dispatch]);

    const normalizedView = urlState.view;
    const isBoreholes3DActive = normalizedView === 'boreholes3d';

    useEffect(() => {
        if (isBoreholes3DActive && !boreholes3DMounted) setBoreholes3DMounted(true);
    }, [isBoreholes3DActive, boreholes3DMounted]);

    // Beim Projektwechsel den Boreholes3D-Cache verwerfen, damit beim nächsten
    // Aufruf mit den neuen Projekt-Daten frisch gemountet wird.
    const prevProjectIdRef = useRef<string | undefined>(selectedProject?.project_id);
    useEffect(() => {
        if (prevProjectIdRef.current !== selectedProject?.project_id) {
            prevProjectIdRef.current = selectedProject?.project_id;
            setBoreholes3DMounted(false);
        }
    }, [selectedProject?.project_id]);

    let rightMenuClick: any;
    let configClick: any;
    let menuClick: any;
    let searchClick: boolean = false;
    let topbarItemClick: any;

    useEffect(() => {
        copyTooltipRef && copyTooltipRef.current && copyTooltipRef.current.updateTargetEvents();
    }, [location]);

    useEffect(() => {
        setResetActiveIndex(true);
        setMenuActive(false);
    }, [menuMode]);

    const onDocumentClick = () => {
        if (!searchClick && searchActive) {
            onSearchHide();
        }

        if (!topbarItemClick) {
            setTopbarMenuActive(false);
        }

        if (!menuClick) {
            if (isHorizontal() || isSlim()) {
                setMenuActive(false);
                setResetActiveIndex(true);
            }

            if (overlayMenuActive || staticMenuMobileActive) {
                setOverlayMenuActive(false);
                setStaticMenuMobileActive(false);
            }

            hideOverlayMenu();
            unblockBodyScroll();
        }

        if (!rightMenuClick) {
            setRightMenuActive(false);
        }

        if (configActive && !configClick) {
            setConfigActive(false);
        }

        topbarItemClick = false;
        menuClick = false;
        configClick = false;
        rightMenuClick = false;
        searchClick = false;
    };

    const onSearchHide = () => {
        setSearchActive(false);
        searchClick = false;
    };

    const onMenuButtonClick = (event: any) => {
        menuClick = true;

        if (isOverlay()) {
            setOverlayMenuActive((prevState: any) => !prevState);
        }

        if (isDesktop()) {
            setStaticMenuDesktopInactive((prevState) => !prevState);
        } else {
            setStaticMenuMobileActive((prevState) => !prevState);
        }

        event.preventDefault();
    };

    const hideOverlayMenu = () => {
        setOverlayMenuActive(false);
        setStaticMenuMobileActive(false);
    };

    const onTopbarItemClick = (event: any) => {
        topbarItemClick = true;
        setTopbarMenuActive((prevState) => !prevState);
        hideOverlayMenu();
        event.preventDefault();
    };

    const onToggleMenu = (event: any) => {
        menuClick = true;

        if (isTablet()) {
            setSidebarActive((prevState: any) => !prevState);
            setSidebarStatic((prevState: any) => !prevState);
        } else {
            if (overlayMenuActive) {
                setOverlayMenuActive(false);
            }

            if (sidebarActive) {
                setSidebarStatic((prevState: any) => !prevState);
            }
        }
        event.preventDefault();
    };

    const onSidebarMouseOver = () => {
        if (menuMode === 'sidebar' && !sidebarStatic && !isTablet()) {
            setSidebarActive(isDesktop());
            setTimeout(() => {
                setPinActive(isDesktop());
            }, 0);
        }
    };

    const onSidebarMouseLeave = () => {
        if (menuMode === 'sidebar' && !sidebarStatic) {
            setTimeout(() => {
                setSidebarActive(false);
                setPinActive(false);
            }, 0);
        }
    };

    const onMenuClick = () => {
        menuClick = true;
    };

    const onChangeActiveInlineMenu = (event: any) => {
        setActiveInlineProfile((prevState) => !prevState);
        event.preventDefault();
    };

    const onRootMenuItemClick = () => {
        setMenuActive((prevState) => !prevState);
    };

    const onMenuItemClick = (event: any) => {
        setCustomBreadcrumb(null);
        if (!event.item.items) {
            hideOverlayMenu();
            setResetActiveIndex(true);
        }

        if (!event.item.items && (isHorizontal() || isSlim())) {
            setMenuActive(false);
        }
    };

    const isHorizontal = () => {
        return menuMode === 'horizontal';
    };

    const isSlim = () => {
        return menuMode === 'slim';
    };

    const isOverlay = () => {
        return menuMode === 'overlay';
    };

    const isDesktop = () => {
        return window.innerWidth > 991;
    };

    const unblockBodyScroll = () => {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    };

    const layoutClassName = classNames('layout-wrapper', {
        'layout-static': menuMode === 'static',
        'layout-overlay': menuMode === 'overlay',
        'layout-overlay-active': overlayMenuActive,
        'layout-slim': menuMode === 'slim',
        'layout-horizontal': menuMode === 'horizontal',
        'layout-active': menuActive,
        'layout-mobile-active': staticMenuMobileActive,
        'layout-sidebar': menuMode === 'sidebar',
        'layout-sidebar-static': menuMode === 'sidebar' && sidebarStatic,
        'layout-static-inactive': staticMenuDesktopInactive && menuMode === 'static',
        'p-ripple-disabled': !ripple
    });

    const menuOptions = [
        {
            id: 'projects',
            label: 'Projects',
            icon: <FontAwesomeIcon className="sidebar-icon" icon={faFolderOpen}/>,
            command: () => navigate({view: 'projects', projectId: null}),
            component: <ProjectOverview/>,
            visible: () => true
        },
        {
            id: 'geomap',
            label: 'GeoMap',
            icon: <FontAwesomeIcon className="sidebar-icon" icon={faMapLocation}/>,
            command: () => navigate({view: 'geomap'}),
            component: <GeoMap/>,
            visible: () => hasProject
        },
        {
            id: 'chart',
            label: 'Chart',
            icon: <FontAwesomeIcon className="sidebar-icon" icon={faChartBar}/>,
            command: () => navigate({view: 'chart'}),
            component: <Chart/>,
            visible: () => hasProject
        },
        {
            id: 'boreholes3d',
            label: '3D Boreholes',
            icon: <FontAwesomeIcon className="sidebar-icon" icon={faGlobeAmericas}/>,
            command: () => navigate({view: 'boreholes3d'}),
            component: <Boreholes3D/>,
            visible: () => hasProject
        },
        {
            id: 'coreviewer',
            label: 'Core Viewer',
            icon: <FontAwesomeIcon className="sidebar-icon" icon={faImages}/>,
            command: () => navigate({view: 'coreviewer'}),
            component: <CoreViewer/>,
            visible: () => hasProject
        },
    ];

    const getComponent = () => {
        let elm = null;
        const view = normalizedView;
        menuOptions.forEach((item: any) => {
            if (item.items) {
                item.items.forEach((item2: any) => {
                    if (item2.id !== 'boreholes3d' && item2.visible() && item2.id === view) {
                        elm = item2.component;
                        return;
                    }
                });
            } else if (item.id !== 'boreholes3d' && item.visible() && item.id === view) {
                elm = item.component;
                return;
            }
        });
        return elm;
    };

    return (
        <div className={layoutClassName} onClick={onDocumentClick}>
            <ConfirmDialog/>
            <Tooltip
                ref={copyTooltipRef}
                target=".block-action-copy"
                position="bottom"
                content="Copied to clipboard"
                event="focus"
            />
            <div className="layout-main ">
                <Topbar
                    items={menuOptions}
                    menuMode={menuMode}
                    menuActive={menuActive}
                    topbarMenuActive={topbarMenuActive}
                    activeInlineProfile={activeInlineProfile}
                    onTopbarItemClick={onTopbarItemClick}
                    onMenuButtonClick={onMenuButtonClick}
                    onSidebarMouseOver={onSidebarMouseOver}
                    onSidebarMouseLeave={onSidebarMouseLeave}
                    onToggleMenu={onToggleMenu}
                    onChangeActiveInlineMenu={onChangeActiveInlineMenu}
                    onMenuClick={onMenuClick}
                    onMenuItemClick={onMenuItemClick}
                    onRootMenuItemClick={onRootMenuItemClick}
                    resetActiveIndex={resetActiveIndex}
                />
                <Menu
                    colorScheme={colorScheme}
                    setColorScheme={setColorScheme}
                    model={menuOptions}
                    onRootMenuItemClick={onRootMenuItemClick}
                    onMenuItemClick={onMenuItemClick}
                    onToggleMenu={onToggleMenu}
                    onMenuClick={onMenuClick}
                    menuMode={menuMode}
                    menuActive={menuActive}
                    sidebarActive={sidebarActive}
                    sidebarStatic={sidebarStatic}
                    pinActive={pinActive}
                    onSidebarMouseLeave={onSidebarMouseLeave}
                    onSidebarMouseOver={onSidebarMouseOver}
                    activeInlineProfile={activeInlineProfile}
                    onChangeActiveInlineMenu={onChangeActiveInlineMenu}
                    resetActiveIndex={resetActiveIndex}
                    setCustomBreadcrumb={setCustomBreadcrumb}
                    onMenuButtonClick={onMenuButtonClick}
                />

                <div className={'layout-main-content ' + normalizedView}>
                    {!isBoreholes3DActive && getComponent()}
                    {boreholes3DMounted && (
                        <div style={{height: '100%', width: '100%', display: isBoreholes3DActive ? 'block' : 'none'}}>
                            <Boreholes3D/>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default App;
